import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CHART_TYPES, DIMENSION_OPTIONS, METRIC_OPTIONS } from "./dashboardConstants";
import InsightDataTable from "./InsightDataTable";
import {
  AXIS_TICK_STYLE,
  CHART_PALETTE,
  DashboardChartTooltip,
  GRID_STYLE,
} from "./rechartsTheme";
import { HEADER_COLOR, POPPINS } from "./dashboardTypography";

const DimensionExplorer = ({ config, data, onConfigChange }) => {
  const renderChart = () => {
    if (config.chartType === "line") {
      return (
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={data}>
            <CartesianGrid {...GRID_STYLE} />
            <XAxis dataKey="dimension" tick={AXIS_TICK_STYLE} />
            <YAxis tick={AXIS_TICK_STYLE} />
            <Tooltip content={<DashboardChartTooltip />} />
            <Legend />
            <Line type="monotone" dataKey="value" stroke="#0f766e" strokeWidth={2} dot={false} connectNulls={false} />
          </LineChart>
        </ResponsiveContainer>
      );
    }

    if (config.chartType === "donut") {
      return (
        <ResponsiveContainer width="100%" height={280}>
          <PieChart>
            <Tooltip content={<DashboardChartTooltip />} />
            <Legend />
            <Pie data={data} dataKey="value" nameKey="dimension" cx="50%" cy="50%" innerRadius={60} outerRadius={95}>
              {data.map((entry, index) => (
                <Cell key={`${entry.dimension}-${index}`} fill={CHART_PALETTE[index % CHART_PALETTE.length]} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      );
    }

    if (config.chartType === "table") {
      return (
        <InsightDataTable
          rows={data}
          columns={[
            { field: "dimension", label: config.dimension },
            { field: "value", label: config.metric },
          ]}
          defaultSortBy="value"
        />
      );
    }

    return (
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={data}>
          <CartesianGrid {...GRID_STYLE} />
          <XAxis dataKey="dimension" tick={AXIS_TICK_STYLE} />
          <YAxis tick={AXIS_TICK_STYLE} />
          <Tooltip content={<DashboardChartTooltip />} />
          <Bar dataKey="value" fill="#0284c7" radius={[6, 6, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  return (
    <Box
      sx={{
        "& .MuiInputBase-root": {
          ...POPPINS,
          fontSize: "0.86rem",
        },
        "& .MuiFormLabel-root": {
          ...POPPINS,
          fontSize: "0.83rem",
        },
        "& .MuiMenuItem-root": {
          ...POPPINS,
          fontSize: "0.82rem",
        },
      }}
    >
      <Typography variant="subtitle1" sx={{ ...POPPINS, color: HEADER_COLOR, fontWeight: 700, mb: 1 }}>
        Dimension Explorer
      </Typography>
      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Dimension</InputLabel>
            <Select
              value={config.dimension}
              label="Dimension"
              onChange={(event) => onConfigChange("dimension", event.target.value)}
            >
              {DIMENSION_OPTIONS.map((value) => (
                <MenuItem key={value} value={value}>{value}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Metric</InputLabel>
            <Select
              value={config.metric}
              label="Metric"
              onChange={(event) => onConfigChange("metric", event.target.value)}
            >
              {METRIC_OPTIONS.map((value) => (
                <MenuItem key={value} value={value}>{value}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small">
            <InputLabel>View</InputLabel>
            <Select
              value={config.chartType}
              label="View"
              onChange={(event) => onConfigChange("chartType", event.target.value)}
            >
              {CHART_TYPES.map((value) => (
                <MenuItem key={value} value={value}>{value.toUpperCase()}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <Stack>{renderChart()}</Stack>
    </Box>
  );
};

DimensionExplorer.propTypes = {
  config: PropTypes.shape({
    dimension: PropTypes.string,
    metric: PropTypes.string,
    chartType: PropTypes.string,
  }).isRequired,
  data: PropTypes.arrayOf(PropTypes.shape({
    dimension: PropTypes.string,
    value: PropTypes.number,
  })),
  onConfigChange: PropTypes.func.isRequired,
};

DimensionExplorer.defaultProps = {
  data: [],
};

export default DimensionExplorer;
